import { useCallback, useState } from 'react';
import { generateAltText } from '../services/api';
import { ImageWithAltText, TextSettings } from '../types/altText';

export const useAltText = () => {
  const [images, setImages] = useState<ImageWithAltText[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateImages = useCallback(
    (uploadedImages: Array<{ imageUrl: string; altText: string }>) => {
      setImages((prev) => {
        const next = uploadedImages.map((newImg) => {
          const existing = prev.find((p) => p.imageUrl === newImg.imageUrl);
          return {
            imageUrl: newImg.imageUrl,
            altText:
              newImg.altText !== undefined
                ? newImg.altText
                : existing?.altText || '',
            isLoading: false,
            error: null,
          };
        });

        const isSame =
          prev.length === next.length &&
          prev.every((p, i) => {
            const n = next[i];
            return (
              p.imageUrl === n.imageUrl &&
              p.altText === n.altText &&
              p.isLoading === n.isLoading &&
              p.error === n.error
            );
          });

        return isSame ? prev : next;
      });
    },
    [],
  );

  const generateAltTexts = useCallback(
    async (
      settings: TextSettings,
      postTitle?: string,
      postContent?: string,
    ) => {
      if (images.length === 0) {
        setError('이미지가 없습니다.');
        return;
      }

      setIsLoading(true);
      setError(null);

      setImages((prev) =>
        prev.map((img) => ({
          ...img,
          isLoading: true,
          error: undefined,
        })),
      );

      try {
        const imageUrls = images.map((img) => img.imageUrl);

        const altTexts = await generateAltText(
          imageUrls,
          settings,
          postTitle || undefined,
          postContent || undefined,
        );

        setImages((prev) =>
          prev.map((img, index) => ({
            ...img,
            altText: index < altTexts.length ? altTexts[index] : '생성 실패',
            isLoading: false,
          })),
        );
      } catch (err) {
        console.error('대체 텍스트 생성 오류:', err);

        setError('대체 텍스트 생성 중 오류가 발생했습니다.');
        setImages((prev) =>
          prev.map((img) => ({
            ...img,
            isLoading: false,
            error: '대체 텍스트 생성 실패',
          })),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [images],
  );

  return {
    images,
    isLoading,
    error,
    updateImages,
    generateAltTexts,
  };
};
